import "primeflex/primeflex.css";
import { useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Fieldset } from "primereact/fieldset";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Paginator } from "primereact/paginator";
import { Message } from "primereact/message";
import { Toast } from "primereact/toast";
import { confirmDialog, ConfirmDialog } from "primereact/confirmdialog";
import { SET_STREET_ABBR } from "../../constants/actionTypes";
import SearchSelect from "../Calculator/SearchSelect";
import useAsyncEffect from "../../hooks/useAsyncEffect";
import agent from "../../agent";
import DialogEditCarrierTerminal from "./DialogEditCarrierTerminal";

const initialFilters = {
  city: { kato: "" },
  cargo: null,
};

const initialPageConfig = {
  page: 0,
  pageSize: 10,
  totalElements: 0,
};

/**
 * Терминалы перевозчиков в opostdb (nsi.terminal).
 *
 * По ним api-market ставит адрес терминала в заказ, а граф решает, может ли
 * перевозчик принять груз на терминал (pickup) и выдать с него (delivery),
 * забрать от двери по городу (doorPickup) и доставить до двери (doorDelivery).
 * Экран «Терминалы ГП/Кросс-докинг» правит другую копию — в slqdb, — и до
 * этой таблицы его правка не доходит.
 */
const CarrierTerminals = () => {
  const dispatch = useDispatch();
  const toast = useRef(null);
  const { streetAbbrOptions } = useSelector((state) => state?.terminals);

  const [postalServices, setPostalServices] = useState([]);
  const [cities, setCities] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [filters, setFilters] = useState(initialFilters);
  const [pageConfig, setPageConfig] = useState(initialPageConfig);
  const [terminals, setTerminals] = useState([]);
  const [loading, setLoading] = useState(false);
  // null — диалог закрыт, {} — новый терминал, строка таблицы — правка
  const [editing, setEditing] = useState(null);

  const showMessage = (severity, summary, detail, life = 5000) =>
    toast.current?.show({ severity, summary, detail, life });

  const fetchTerminals = async (psId, kato, page = 0, pageSize = 10) => {
    setLoading(true);
    try {
      const data = await agent.NsiTerminals.getTerminals(psId || "", kato || "", page, pageSize);
      setPageConfig({
        page: data?.number ?? 0,
        pageSize: data?.size ?? pageSize,
        totalElements: data?.totalElements ?? 0,
      });
      setTerminals(data?.content || []);
    } catch (err) {
      showMessage("error", "Ошибка", agent.handleError(err));
    } finally {
      setLoading(false);
    }
  };

  const fetchCities = async (type, value) => {
    const data = await agent.NsiTerminals.searchCity(value);
    if (type === "all") {
      setCities(data || []);
    }
    setCityOptions(data || []);
  };

  useAsyncEffect(async () => {
    // Перевозчики — из opostdb, где лежат и сами терминалы: id продуктов там
    // свои, не те, что в calc-service. У продуктов одного перевозчика названия
    // совпадают, поэтому к названию добавлен код продукта.
    const postal = await agent.PostalService.all();
    setPostalServices((postal || []).map((ps) => ({ ...ps, label: `${ps.nameRu} (${ps.productCode})` })));
    const abbr = await agent.StreetAbbr.all();
    dispatch({ type: SET_STREET_ABBR, payload: abbr });
    await fetchCities("all", "");
    await fetchTerminals("", "", 0, initialPageConfig.pageSize);
  }, []);

  const reload = () => fetchTerminals(filters.cargo, filters.city?.kato, pageConfig.page, pageConfig.pageSize);

  const handleFind = () => fetchTerminals(filters.cargo, filters.city?.kato, 0, pageConfig.pageSize);

  const handleClean = () => {
    setFilters(initialFilters);
    setCityOptions(cities);
    fetchTerminals("", "", 0, pageConfig.pageSize);
  };

  const onSaved = async () => {
    setEditing(null);
    await fetchCities("all", "");
    await reload();
  };

  const onDelete = (row) => {
    confirmDialog({
      header: "Удаление терминала",
      icon: "pi pi-exclamation-triangle",
      message: `Удалить терминал ${carrierName(row)}: ${cityName(row)}, ${row.address || ""}? Граф перестанет считать, что перевозчик принимает и выдаёт здесь груз.`,
      acceptLabel: "Удалить",
      rejectLabel: "Отмена",
      acceptClassName: "p-button-danger",
      accept: async () => {
        try {
          await agent.NsiTerminals.deleteTerminal(row.id);
          showMessage("success", "Удаление терминала", "Терминал удалён");
          await reload();
        } catch (err) {
          showMessage("error", "Терминал не удалён", agent.handleError(err));
        }
      },
    });
  };

  const carrierName = (row) => postalServices.find((ps) => ps.id === row.postal_service_id)?.label || "";
  const cityName = (row) => cities.find((city) => city.kato === row.kato)?.name || row.kato;
  const streetType = (row) => streetAbbrOptions?.find((abbr) => abbr.id === row.street_abbr_id)?.nameRu || "";
  const yesNo = (value) => (value ? "да" : <span style={{ color: "#b91c1c" }}>нет</span>);

  const actionsBody = (row) => (
    <div className="p-d-flex p-jc-center">
      <Button onClick={() => setEditing(row)} className="p-button-text p-button-rounded" icon="pi pi-pencil" />
      <Button onClick={() => onDelete(row)} className="p-button-text p-button-rounded p-ml-2" icon="pi pi-trash" />
    </div>
  );

  return (
    <div>
      <Toast ref={toast} />
      <ConfirmDialog />

      {editing && (
        <DialogEditCarrierTerminal
          editTerminal={editing.id ? editing : null}
          cities={cities}
          streetAbbrOptions={streetAbbrOptions}
          postalServices={postalServices}
          showMessage={showMessage}
          onSuccess={onSaved}
          onHide={() => setEditing(null)}
        />
      )}

      <div className="p-d-flex p-jc-between p-ai-center">
        <h2>Терминалы перевозчиков</h2>
        <Button label="Добавить терминал" icon="pi pi-plus" onClick={() => setEditing({})} />
      </div>

      <Message
        severity="info"
        style={{ marginBottom: 10 }}
        text="Терминалы в opostdb: по ним граф решает, может ли перевозчик принять груз на терминал и выдать с него, а заказ получает адрес терминала. Экран «Терминалы ГП/Кросс-докинг» правит другую базу (slqdb), правки между экранами не переносятся."
      />

      <Fieldset legend="Фильтр" toggleable>
        <div className="p-fluid p-formgrid p-grid p-mb-3">
          <div className="p-col-4 p-mr-3 p-d-flex p-flex-column">
            <label className="p-mb-1" htmlFor="city">Город</label>
            <SearchSelect
              placeholder=""
              name="name"
              state={filters.city}
              setState={(value) => setFilters((prev) => ({ ...prev, city: value }))}
              options={cityOptions}
              optionValue={"kato"}
              onChange={(value) => fetchCities("search", value)}
              optionText={"name"}
            />
          </div>
          <div className="p-col-4 p-d-flex p-flex-column">
            <label className="p-mb-1" htmlFor="psId">Перевозчик</label>
            <Dropdown
              inputId="psId"
              value={filters.cargo}
              options={postalServices}
              onChange={(e) => setFilters((prev) => ({ ...prev, cargo: e.value }))}
              optionLabel="label"
              optionValue="id"
              filter
              showClear
            />
          </div>
        </div>
        <div className="p-grid p-justify-end">
          <div className="p-mr-2">
            <Button className="p-button-warning" label="Сбросить" onClick={handleClean} />
          </div>
          <div className="p-mr-2">
            <Button onClick={handleFind} label="Найти" />
          </div>
        </div>
      </Fieldset>

      <DataTable value={terminals} loading={loading} dataKey="id" rowHover emptyMessage="Данные не найдены" style={{ marginTop: 16 }}>
        <Column header="Перевозчик" body={carrierName} />
        <Column header="Город" body={cityName} />
        <Column header="Адрес" body={(row) => `${streetType(row)} ${row.address || ""}`.trim()} />
        <Column header="Принимает" body={(row) => yesNo(row.pickup)} />
        <Column header="Выдаёт" body={(row) => yesNo(row.delivery)} />
        <Column header="Забор от двери" body={(row) => yesNo(row.doorPickup ?? true)} />
        <Column header="Доставка до двери" body={(row) => yesNo(row.doorDelivery ?? true)} />
        <Column field="codeDPD" header="Код DPD" />
        <Column field="time" header="Время работы" />
        <Column body={actionsBody} style={{ width: "7rem" }} />
      </DataTable>
      <Paginator
        currentPageReportTemplate="Показано с {first} по {last} из {totalRecords} записей"
        template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
        first={pageConfig.page * pageConfig.pageSize}
        rows={pageConfig.pageSize}
        totalRecords={pageConfig.totalElements}
        rowsPerPageOptions={[10, 25, 50]}
        onPageChange={(e) => fetchTerminals(filters.cargo, filters.city?.kato, e.page, e.rows)}
      />
    </div>
  );
};

export default CarrierTerminals;
